import { useCallback, useMemo } from 'react';

import neighboursAreaBannerButtonClick from '@hh.ru/analytics-js-events/build/xhh/common/vacancy_search/neighbours_area_banner/neighbours_area_banner_button_click';
import neighboursAreaBannerElementShown from '@hh.ru/analytics-js-events/build/xhh/common/vacancy_search/neighbours_area_banner/neighbours_area_banner_element_shown';
import { Banner, Button, Text, Title, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import urlParser from 'bloko/common/urlParser';

import paths from 'src/app/routePaths';
import ElementShownAnchor from 'src/components/ElementShownAnchor';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { EMPTY_CLUSTER } from 'src/models/search/common/clusters';
import { ClusterKey } from 'src/types/search/common/clusters';

const TrlKeys = {
    title: {
        notEmpty: 'neighbours.area.banner.title.notEmptyResult',
        empty: 'neighbours.area.banner.title.emptyResult',
    },
    description: {
        notEmpty: 'neighbours.area.banner.description.notEmptyResult',
        empty: 'neighbours.area.banner.description.emptyResult',
    },
    button: 'neighbours.area.banner.button',
};

const EMPTY_ARRAY = [] as const;

const NeighboursAreaBanner: TranslatedComponent = ({ trls }) => {
    const currentArea = useSelector((state) => state.searchClusters?.[ClusterKey.Area]?.selectedValues) || EMPTY_ARRAY;
    const clusterNeighbours = useSelector((state) => state.searchClusters?.[ClusterKey.Neighbours]) || EMPTY_CLUSTER;
    const totalResults = useSelector((state) => state.vacancySearchResult.totalResults);
    const currentParams = useSelector((state) => state.vacancySearchResult.queries.currentParams);
    const searchSessionId = useSelector((state) => state.searchSessionId);

    const analyticsData = useMemo(
        () => ({
            currentArea: currentArea.length ? currentArea[0] : 0,
            neighbours: Object.keys(clusterNeighbours.groups).join(','),
            totalResults,
            searchSessionId: searchSessionId || '',
        }),
        [clusterNeighbours, currentArea, searchSessionId, totalResults]
    );

    const push = usePush();

    const { isMobile } = useBreakpoint();

    const handleButtonClick = useCallback(() => {
        const url = urlParser(paths.vacancySearch);
        url.search = currentParams || '';
        const neighbours = Object.keys(clusterNeighbours.groups);
        url.params.area = [...currentArea, ...neighbours];
        neighboursAreaBannerButtonClick(analyticsData);
        push(url.href);
    }, [analyticsData, clusterNeighbours, currentArea, currentParams, push]);

    return (
        <>
            <ElementShownAnchor fn={neighboursAreaBannerElementShown} {...analyticsData}>
                <Banner
                    stretched
                    showClose={false}
                    style="accent-secondary"
                    title={
                        <Title size="small" Element="h4">
                            {trls[totalResults > 0 ? TrlKeys.title.notEmpty : TrlKeys.title.empty]}
                        </Title>
                    }
                    content={
                        <Text>{trls[totalResults > 0 ? TrlKeys.description.notEmpty : TrlKeys.description.empty]}</Text>
                    }
                    buttonBase={
                        <Button
                            mode="primary"
                            style="accent"
                            size={isMobile ? 'medium' : 'small'}
                            onClick={handleButtonClick}
                            stretched={isMobile}
                        >
                            {trls[TrlKeys.button]}
                        </Button>
                    }
                />
            </ElementShownAnchor>
            <VSpacing default={24} />
        </>
    );
};

export default translation(NeighboursAreaBanner);
